/* eslint-disable no-unused-vars */
import React, {
  MouseEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import BackIcon from "@svg/player/Back";
import FastIcon from "@svg/player/Fast";
import VolumeHigh from "@svg/player/VolumeHigh";
import Mute from "@svg/player/Mute";
import ProgressBar, { ProgressBarElement } from "./progress-bar";
import { formatTime } from "@utils/player";
import {
  PlayerSettings,
  SEEK_STATUS,
  Source,
  SpeedRate,
  SpeedRateItem,
} from "@types";
import { toFarsiNumber } from "@utils/digits";
import NextTimer from "./next-timer";
import BackwardIcon from "@svg/backward-icon";
import ForwardIcon from "@svg/forward-icon";
import useIsPlatformMobile from "@shared/hooks/useIsPlatformMobile";
import VideoPlayerPlayButtonInMobile from "./components/PlayButtonInMobile";
import VideoControlsBottomActions from "./components/BottomActions";
import CenteredPlayButton from "./components/CenteredPlayButton";
import Tooltip from "@shared/components/tooltip/Tooltip";
import { TOOLTIP_THEME } from "@shared/components/tooltip/constants";

export const PLAY_RATES: SpeedRateItem[] = [
  { label: "0/5x", id: 0.5, show: false },
  { label: "0/75x", id: 0.75, show: true },
  { label: "1x", id: 1, show: true },
  { label: "1/25x", id: 1.25, show: true },
  { label: "1/5x", id: 1.5, show: true },
  { label: "1/75x", id: 1.75, show: false },
  { label: "2x", id: 2, show: true },
];

interface Props {
  isMobile: boolean;
  isPlaying: boolean;
  isLoading: boolean;
  isControlsVisible?: boolean;
  settings: PlayerSettings;
  sources: Source[];
  videoNode: HTMLVideoElement | null;
  animatePlay: boolean;
  nextLessonName?: string;
  showNext?: boolean;
  showTheater?: boolean;
  theaterMode: boolean;
  speedRateChangeHandler: (rate: SpeedRate) => void;
  qualityChangeHandler: (quality: string) => void;
  volumeChangeHandler: (volume: number) => void;
  toggleFullscreenHandler: () => void;
  toggleTheaterHandler: () => void;
  togglePipHandler: () => void;
  togglePlayByClickHandler: (
    e: MouseEvent<HTMLElement | SVGSVGElement>
  ) => void;
  toggleMuteHandler: () => void;
  handleSeekTo: (time: number) => void;
  backOrForWardHandler: (backward: boolean) => void;
  toggleSubtitle: () => void;
  goNext?: (autoplay?: boolean) => void;
  seekAnimate: SEEK_STATUS | undefined;
  showControls?: () => void;
}

export type ControlsElement = {
  container: HTMLDivElement | null;
  updateDuration: (duration: number) => void;
  updateProgress: (time: number, duration: number, maskTime?: number) => void;
  updateBuffer: (time: number, duration: number) => void;
  updateSeekData: (offset: number, duration: number) => void;
  videoEnded: (hasNext: boolean) => void;
};

const Controls = React.forwardRef<ControlsElement, Props>(
  (
    {
      showNext,
      isLoading,
      isPlaying,
      isControlsVisible,
      settings,
      sources,
      videoNode,
      animatePlay,
      nextLessonName = "",
      speedRateChangeHandler,
      qualityChangeHandler,
      toggleFullscreenHandler,
      volumeChangeHandler,
      togglePipHandler,
      togglePlayByClickHandler,
      toggleMuteHandler,
      handleSeekTo,
      backOrForWardHandler,
      toggleSubtitle,
      goNext,
      seekAnimate,
      showControls,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const progressBarRef = useRef<ProgressBarElement | null>(null);
    const elapsedRef = useRef<HTMLTimeElement | null>(null);
    const durationRef = useRef<HTMLTimeElement | null>(null);
    const nextTimerRef = useRef<HTMLSpanElement | null>(null);
    const [isLandscape, setIsLandscape] = useState(false);
    const [showGoNextTimer, setShowGoNextTimer] = useState(false);

    const { fullScreen, isMuted } = settings;

    const isMobile = useIsPlatformMobile();

    const controlsIconProps = {
      width: isMobile ? 32 : 40,
      height: isMobile ? 32 : 40,
      fill: "#ffffff",
    };

    useEffect(() => {
      const onResize = () => {
        const viewportWidth =
          window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;
        setIsLandscape(viewportWidth > viewportHeight);
      };

      const supportsOrientationChange = "onorientationchange" in window;
      const orientationEvent = supportsOrientationChange
        ? "orientationchange"
        : "resize";

      document.addEventListener(orientationEvent, onResize);
      onResize();
      return () => {
        window.removeEventListener(orientationEvent, onResize);
      };
    }, []);

    const prepareTime = (time: {
      minutes: string;
      seconds: string;
      hours: string;
    }) => {
      const { hours, minutes, seconds } = time;
      return hours != "00"
        ? toFarsiNumber(hours) +
            `:` +
            toFarsiNumber(minutes) +
            `:` +
            toFarsiNumber(seconds)
        : toFarsiNumber(minutes) + `:` + toFarsiNumber(seconds);
    };

    const updateDuration = (duration: number) => {
      if (durationRef.current) {
        const time = formatTime(duration);
        const { hours, minutes, seconds } = time;

        durationRef.current.innerText = prepareTime(time);
        durationRef.current.dateTime =
          hours != "00"
            ? `${hours}h ${minutes}m ${seconds}s`
            : `${minutes}m ${seconds}s`;
      }
      progressBarRef.current?.updateDuration(duration);
    };

    const updateProgress = (
      t: number,
      duration: number,
      maskTime: number | undefined
    ) => {
      const time = formatTime(Math.round(t));
      if (elapsedRef.current) {
        const { hours, minutes, seconds } = time;
        elapsedRef.current.innerText = prepareTime(time);
        elapsedRef.current.dateTime =
          hours != "00"
            ? `${hours}h ${minutes}m ${seconds}s`
            : `${minutes}m ${seconds}s`;
      }
      progressBarRef.current?.updateProgress(t, duration, maskTime);
      setShowGoNextTimer(false);
      if (nextTimerRef.current)
        nextTimerRef.current.innerText =
          toFarsiNumber(Math.round(duration - t + 1)) + " ثانیه ";
    };

    useImperativeHandle(
      ref,
      () => ({
        container: containerRef.current,
        updateDuration: updateDuration,
        updateProgress: updateProgress,
        updateBuffer: (time, duration) => {
          progressBarRef.current?.updateBuffer(time, duration);
        },
        updateSeekData: (offset, duration) =>
          progressBarRef.current?.updateSeekData(offset, duration),
        videoEnded: (hasNext) => {
          setShowGoNextTimer(hasNext);
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const handleControlsMouseUp = () => {
      progressBarRef.current?.seekTo();
    };

    const onGoNext = (autoplay?: boolean) => {
      goNext?.(autoplay);
      setShowGoNextTimer(false);
    };

    const seekStatus = {
      [SEEK_STATUS.FORWARD]: (
        <>
          <ForwardIcon animate width={20} height={20} fill="#fff" />
          <span>۱۰ ثانیه بعد</span>
        </>
      ),
      [SEEK_STATUS.BACKWARD]: (
        <>
          <BackwardIcon animate width={20} height={20} fill="#fff" />
          <span>۱۰ ثانیه قبل</span>
        </>
      ),
    };
    return (
      <>
        <div
          className={classNames(
            "nplay-video-controls_bg",
            isControlsVisible || isControlsVisible === undefined
              ? "opacity-100"
              : "opacity-0"
          )}
        />

        {showGoNextTimer ? (
          <NextTimer
            nextName={nextLessonName}
            onClose={() => setShowGoNextTimer(false)}
            onGoNext={onGoNext}
          />
        ) : (
          <>
            {isMobile && (
              <div
                className={classNames(
                  "nplay-volume-button nplay-mobile-voice-mute flex justify-center items-center",
                  fullScreen ? "msm:bottom-[36rem]" : "msm:mb-0",
                  isControlsVisible || isControlsVisible === undefined
                    ? ""
                    : "hidden"
                )}
                onPointerDown={showControls}
                onClick={toggleMuteHandler}
              >
                {isMuted ? (
                  <Mute fill="#f5f5f5" />
                ) : (
                  <VolumeHigh fill="#f5f5f5" />
                )}
              </div>
            )}
            {isControlsVisible !== undefined && (
              <div
                className={classNames(
                  "nplay-mobile-playback-icons top-[50%]",
                  !isControlsVisible && "hidden"
                )}
                onPointerDown={showControls}
              >
                {!isMobile && (
                  <BackIcon
                    onClick={() => backOrForWardHandler(true)}
                    className={classNames(
                      "nply-jumptf",
                      !isLandscape ? "" : "hidden"
                    )}
                    {...controlsIconProps}
                  />
                )}
                <VideoPlayerPlayButtonInMobile
                  isMobile={isMobile}
                  isPlaying={isPlaying}
                  togglePlayByClickHandler={togglePlayByClickHandler}
                  controlsIconProps={controlsIconProps}
                />
                {!isMobile && (
                  <Tooltip
                    theme={TOOLTIP_THEME.LIGHT}
                    showArrow={false}
                    disabled={isMobile}
                    message={
                      <p className="flex flex-row-reverse">
                        <span className="ml-0.5">۱۰</span>
                        <span> ثانیه بعد </span>
                      </p>
                    }
                  >
                    <button onClick={() => backOrForWardHandler(false)}>
                      <FastIcon
                        className={classNames(
                          "nply-jumptb",
                          !isLandscape ? "" : "hidden"
                        )}
                        {...controlsIconProps}
                      />
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
            {(!isMobile || isControlsVisible === undefined) && (
              <CenteredPlayButton
                isPlaying={isPlaying}
                animatePlay={animatePlay}
                isControlsVisible={isControlsVisible}
              />
            )}
            <div
              className={classNames(
                "nplay-seek-animation",
                seekAnimate ? seekAnimate.toLowerCase() : ""
              )}
            >
              {seekAnimate ? seekStatus[seekAnimate] : ""}
            </div>
            <div
              className={classNames("nplay-loader", isLoading ? "" : "hidden")}
            />
          </>
        )}
        <div
          className={classNames(
            "nplay-video-controls",
            isControlsVisible ? "" : "hide",
            showGoNextTimer ? "smx:hidden" : ""
          )}
          ref={containerRef}
          onPointerDown={showControls}
          onMouseUp={handleControlsMouseUp}
        >
          <ProgressBar
            onSeekTo={handleSeekTo}
            ref={progressBarRef}
            videoNode={videoNode}
            fullScreen={fullScreen}
          />
          <VideoControlsBottomActions
            isMobile={isMobile}
            isPlaying={isPlaying}
            controlsIconProps={controlsIconProps}
            showGoNextTimer={showGoNextTimer}
            settings={settings}
            sources={sources}
            isLandscape={isLandscape}
            elapsedRef={elapsedRef}
            durationRef={durationRef}
            togglePlayByClickHandler={togglePlayByClickHandler}
            backOrForWardHandler={backOrForWardHandler}
            toggleMuteHandler={toggleMuteHandler}
            volumeChangeHandler={volumeChangeHandler}
            speedRateChangeHandler={speedRateChangeHandler}
            qualityChangeHandler={qualityChangeHandler}
            toggleFullscreenHandler={toggleFullscreenHandler}
            toggleSubtitle={toggleSubtitle}
            togglePipHandler={togglePipHandler}
          />
        </div>
        {showNext && nextLessonName ? (
          <div
            style={{ direction: "rtl" }}
            className="absolute flex cursor-default bottom-[102px] bg-black/[0.55]  flex-wrap right-0 z-[1] mx-4 md:mx-0 "
          >
            <p className="text-white py-4 px-2.5 h-14 rounded-r-lg ">
              درس بعدی : {nextLessonName}
            </p>
          </div>
        ) : null}
      </>
    );
  }
);

Controls.displayName = "Controls";
export default Controls;
